import axios from "axios";
import {
  GET_EXAM_SUBSCRIPTIONS_REQUEST,
  GET_EXAM_SUBSCRIPTIONS_SUCCESS,
  GET_EXAM_SUBSCRIPTIONS_FAIL,
  GET_SUBSCRIPTION_REQUEST,
  GET_SUBSCRIPTION_SUCCESS,
  GET_SUBSCRIPTION_FAIL,
  USER_SUBSCRIPTION_REQUEST,
  USER_SUBSCRIPTION_SUCCESS,
  USER_SUBSCRIPTION_FAIL,
  PLAN_SUBSCRIPTION_REQUEST,
  PLAN_SUBSCRIPTION_SUCCESS,
  PLAN_SUBSCRIPTION_FAIL,
  USER_SUBSCRIPTION_LIST_REQUEST,
  USER_SUBSCRIPTION_LIST_SUCCESS,
  USER_SUBSCRIPTION_LIST_FAIL,
  CLEAR_ERRORS,
} from "../constants/subscriptionConstants";
import {
  subscriptionMatchesExam,
} from "../utils/examSubscription";

// Normalize API base to avoid double slashes when concatenating paths
const __raw_api_path = process.env.REACT_APP_API_BASE_URL || "/api/v1";
const _api_path = __raw_api_path.replace(/\/+$/, "");
const _authConfig = { withCredentials: true };

//get exam all subscriptions
export const getExamSubscriptions = (examId, userId) => async (dispatch) => {
  try {
    dispatch({
      type: GET_EXAM_SUBSCRIPTIONS_REQUEST,
    });

    const { data } = await axios.get(
      _api_path + `/get-subscriptions/${examId}/${userId}`
    );

    dispatch({
      type: GET_EXAM_SUBSCRIPTIONS_SUCCESS,
      payload: data.data,
    });
  } catch (error) {
    dispatch({
      type: GET_EXAM_SUBSCRIPTIONS_FAIL,
      payload:
        error?.response?.data?.errMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to fetch exam subscriptions.",
    });
  }
};

//get user subscription
export const getUserSubscription = (userId, examId, universityId = null) => async (dispatch) => {
  try {
    dispatch({
      type: USER_SUBSCRIPTION_REQUEST,
    });

    // Same endpoint as getUserSubscriptionList — `/user-subscriptions/:id` does not exist
    const { data } = await axios.get(
      `${_api_path}/user-subscription-list/${userId}`,
      _authConfig
    );

    const subscriptions = Array.isArray(data?.userSubscription)
      ? data.userSubscription
      : Array.isArray(data?.data)
        ? data.data
        : [];

    const normalizedExamId = examId ? String(examId) : null;
    const normalizedUniversityId = universityId ? String(universityId) : null;

    const activeSubscription = subscriptions.find((subscription) => {
      if (!subscription) {
        return false;
      }

      const isExamMatch = subscriptionMatchesExam(subscription, normalizedExamId);
      const subscriptionUniversityId = subscription?.university?._id || subscription?.university || null;

      const isUniversityMatch = normalizedUniversityId
        ? subscriptionUniversityId
          ? String(subscriptionUniversityId) === normalizedUniversityId
          : true
        : true;

      if (!isExamMatch || !isUniversityMatch) {
        return false;
      }

      if (subscription?.status === false) {
        return false;
      }

      if (subscription?.expireAt) {
        const expiresOn = new Date(subscription.expireAt);
        if (!isNaN(expiresOn.getTime()) && expiresOn < new Date()) {
          return false;
        }
      }

      const subscribedSubjects = Array.isArray(subscription?.subjectsProcessed)
        ? subscription.subjectsProcessed.length
        : Array.isArray(subscription?.subjects)
          ? subscription.subjects.length
          : 0;

      const maxAllowed = subscription?.exam?.maximumSubjects || subscription?.maximumSubjects || 0;

      if (maxAllowed && subscribedSubjects >= maxAllowed) {
        subscription._allSubjectsUsed = true;
      }

      return true;
    }) || null;

    const payload = {
      userSubscription: activeSubscription,
      subscriptions,
    };

    dispatch({
      type: USER_SUBSCRIPTION_SUCCESS,
      payload,
    });

    return payload;
  } catch (error) {
    const errMessage =
      error?.response?.data?.errMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Unable to fetch subscription.";

    dispatch({
      type: USER_SUBSCRIPTION_FAIL,
      payload: errMessage,
    });

    return { success: false, error: errMessage, isError: true };
  }
};

//subscribe the plan
export const userSubscribePlan =
  (examId, universityId, subscriptionId, userId, paymentData) => async (dispatch) => {
    try {
      dispatch({
        type: PLAN_SUBSCRIPTION_REQUEST,
      });
      const config = {
        headers: {
          "content-type": "application/json",
        },
      };
      const resolvedUniversityId =
        universityId && String(universityId).trim() !== "" ? universityId : null;
      const { data } = await axios.post(
        _api_path + "/subscribe-plan",
        {
          examId,
          universityId: resolvedUniversityId,
          subscriptionId,
          userId,
          paymentData
        },
        config
      );
      const subscriptions = data?.userSubscriptions || [];
      const primarySubscription = subscriptions[0] || null;

      dispatch({
        type: PLAN_SUBSCRIPTION_SUCCESS,
        payload: {
          subscriptions,
          primarySubscription,
        },
      });

      return {
        success: true,
        subscriptions,
        primarySubscription,
      };
    } catch (error) {
      dispatch({
        type: PLAN_SUBSCRIPTION_FAIL,
        payload:
          error?.response?.data?.errMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to subscribe to plan.",
      });

      throw error;
    }
  };

//get user subscriptions
export const getUserSubscriptionList = (userId, options = {}) => async (dispatch) => {
  try {
    dispatch({
      type: USER_SUBSCRIPTION_LIST_REQUEST,
    });

    const includeInactive =
      typeof options === "object" && options?.includeInactive ? "true" : "false";

    const { data } = await axios.get(
      `${_api_path}/user-subscription-list/${userId}?includeInactive=${includeInactive}`,
      _authConfig
    );
    dispatch({
      type: USER_SUBSCRIPTION_LIST_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_SUBSCRIPTION_LIST_FAIL,
      payload:
        error?.response?.data?.errMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to fetch subscriptions.",
    });
  }
};

//get subscription details by id
export const getSubscriptionById = (subscriptionId) => async (dispatch) => {
  try {
    dispatch({
      type: GET_SUBSCRIPTION_REQUEST,
    });

    const { data } = await axios.get(
      _api_path + `/get-subscription/${subscriptionId}`
    );

    dispatch({
      type: GET_SUBSCRIPTION_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: GET_SUBSCRIPTION_FAIL,
      payload:
        error?.response?.data?.errMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to fetch subscription details.",
    });
  }
};

//clear errors
export const clearErrors = () => async (dispatch) => {
  dispatch({
    type: CLEAR_ERRORS,
  });
};
